import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';
import {
  FaCommentDots,
  FaMicrophone,
  FaPaperPlane,
  FaSpinner,
  FaStop,
  FaTimes,
  FaVolumeUp,
} from 'react-icons/fa';
import './CSS/PampaChatWidget.css';

const LANGUAGES = [
  { label: 'English', value: 'en-IN' },
  { label: 'Hindi', value: 'hi-IN' },
  { label: 'Tamil', value: 'ta-IN' },
  { label: 'Telugu', value: 'te-IN' },
  { label: 'Kannada', value: 'kn-IN' },
  { label: 'Malayalam', value: 'ml-IN' },
  { label: 'Marathi', value: 'mr-IN' },
  { label: 'Gujarati', value: 'gu-IN' },
  { label: 'Bengali', value: 'bn-IN' },
];

function readJsonLS(key, fallback) {
  const raw = localStorage.getItem(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export default function PampaChatWidget() {
  const location = useLocation();
  const isPampaPage = location.pathname.startsWith('/admin/pampa');
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const name = useMemo(() => String(readJsonLS('name', 'User') || 'User'), []);
  const bunkName = useMemo(
    () => String(readJsonLS('bunkname', 'Fuel Station') || 'Fuel Station'),
    []
  );

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Hi, I am PAMPA. Ask me anything about FuelFlow and I will try to help.',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLang, setSelectedLang] = useState('en-IN');
  const [speakReplies, setSpeakReplies] = useState(true);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  };

  useEffect(() => {
    if (!open) return;
    scrollToBottom();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, messages, loading]);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition || null;

    if (!SpeechRecognition) return undefined;

    const rec = new SpeechRecognition();
    rec.interimResults = true;
    rec.continuous = false;
    rec.lang = selectedLang;

    rec.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript.trim());
    };

    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;

    return () => {
      try {
        rec.stop();
      } catch {
        // ignore
      }
    };
  }, [selectedLang]);

  useEffect(() => {
    if (!recognitionRef.current) return;
    recognitionRef.current.lang = selectedLang;
  }, [selectedLang]);

  useEffect(() => () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // ignore
      }
      audioRef.current = null;
    }
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch {
        // ignore
      }
      audioRef.current = null;
    }
    setSpeaking(false);
  };

  const speak = async (text, langHint = selectedLang) => {
    if (!speakReplies) return;

    try {
      setSpeaking(true);
      stopAudio();

      const res = await api.post(
        '/api/pampa/speak',
        { text, locale: langHint },
        { headers, responseType: 'blob' }
      );

      const blob = new Blob([res.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setSpeaking(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setSpeaking(false);
      };
      await audio.play();
    } catch (err) {
      setSpeaking(false);
      setError(err?.response?.data?.error || 'Could not play PAMPA voice right now.');
    }
  };

  const sendMessage = async (messageText = input) => {
    const text = String(messageText || '').trim();
    if (!text || loading) return;

    setError('');
    setLoading(true);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);

    try {
      const res = await api.post(
        '/api/pampa/chat',
        { message: text, locale: selectedLang },
        { headers }
      );

      const payload = res.data || {};
      const reply = payload.reply || 'I have processed your request.';
      const responseLocale = payload.responseLocale || selectedLang;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: reply,
          meta: {
            cards: payload.cards || [],
            table: payload.table || null,
            suggestions: payload.suggestions || [],
          },
        },
      ]);

      await speak(reply, responseLocale);
    } catch (err) {
      const errText =
        err?.response?.data?.error || 'PAMPA could not answer right now. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', text: errText }]);
      setError(errText);
    } finally {
      setLoading(false);
    }
  };

  const createSupportTicket = async () => {
    const text = String(input || '').trim();
    if (!text || loading) {
      setError('Please describe the issue before creating a support ticket.');
      return;
    }

    setError('');
    setLoading(true);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);

    try {
      const response = await api.post(
        '/api/support-tickets',
        {
          title: text.slice(0, 120),
          description: text,
          priority: 'high',
          source: 'bot',
          channel: listening ? 'voice' : 'web',
        },
        { headers }
      );

      const ticketNo = response.data?.ticket_number || 'support case';
      const reply = `Done. I created ${ticketNo} and sent it to the support queue.`;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: reply,
          meta: {
            cards: [
              { label: 'Ticket No', value: ticketNo },
              { label: 'Status', value: 'open' },
              { label: 'Priority', value: 'high' },
            ],
          },
        },
      ]);

      await speak(reply, selectedLang);
    } catch (err) {
      const errText =
        err?.response?.data?.error || 'Could not create the support ticket right now.';
      setMessages((prev) => [...prev, { role: 'assistant', text: errText }]);
      setError(errText);
    } finally {
      setLoading(false);
    }
  };

  const startListening = () => {
    if (!recognitionRef.current) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }

    try {
      setError('');
      setListening(true);
      recognitionRef.current.lang = selectedLang;
      recognitionRef.current.start();
    } catch {
      setListening(false);
      setError('Could not start voice recognition. Please try again.');
    }
  };

  const stopListening = () => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore
    }
    setListening(false);
  };

  if (isPampaPage) return null;

  return (
    <div className="pampa-float-shell">
      {open ? (
        <div className="pampa-float-panel" role="dialog" aria-label="PAMPA assistant">
          <div className="pampa-float-header">
            <div className="pampa-float-title">
              <div className="pampa-float-avatar">
                <FaCommentDots aria-hidden="true" />
              </div>
              <div>
                <strong>PAMPA</strong>
                <span>
                  {bunkName} - {name}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="pampa-float-close"
              aria-label="Close PAMPA chat"
              onClick={() => setOpen(false)}
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>

          <div className="pampa-float-toolbar">
            <label className="pampa-float-select">
              <span>Language</span>
              <select value={selectedLang} onChange={(e) => setSelectedLang(e.target.value)}>
                {LANGUAGES.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className={`pampa-float-pill ${speakReplies ? 'active' : ''}`}
              onClick={() => setSpeakReplies((v) => !v)}
            >
              <FaVolumeUp aria-hidden="true" />
              <span>{speakReplies ? 'Voice On' : 'Voice Off'}</span>
            </button>
          </div>

          {error ? <div className="pampa-float-error">{error}</div> : null}

          <div className="pampa-float-messages" role="log" aria-live="polite">
            {messages.map((message, idx) => (
              <article key={`${message.role}-${idx}`} className={`pampa-float-bubble ${message.role}`}>
                <div className="pampa-float-badge">{message.role === 'user' ? 'You' : 'PAMPA'}</div>
                <p>{message.text}</p>

                {message.role === 'assistant' && message.meta?.cards?.length ? (
                  <div className="pampa-float-cards">
                    {message.meta.cards.map((card) => (
                      <div key={card.label} className="pampa-float-stat">
                        <span>{card.label}</span>
                        <strong>{String(card.value ?? '-')}</strong>
                      </div>
                    ))}
                  </div>
                ) : null}

                {message.role === 'assistant' && message.meta?.table?.rows?.length ? (
                  <div className="pampa-float-table-wrap">
                    <table className="table table-sm table-dark align-middle mb-0">
                      <thead>
                        <tr>
                          {message.meta.table.columns.map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {message.meta.table.rows.map((row, rowIndex) => (
                          <tr key={`pampa-row-${rowIndex}`}>
                            {message.meta.table.columns.map((col) => (
                              <td key={`${rowIndex}-${col}`}>{row[col] ?? '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

              </article>
            ))}

            {loading ? (
              <div className="pampa-float-bubble assistant">
                <div className="pampa-float-badge">PAMPA</div>
                <p>Thinking...</p>
              </div>
            ) : null}
            <div ref={bottomRef} />
          </div>

          <form
            className="pampa-float-compose"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
          >
            <textarea
              ref={inputRef}
              className="form-control pampa-float-input"
              rows={2}
              placeholder="Ask about reports or say you need support..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />

            <div className="pampa-float-actions">
              <span className={`pampa-float-status ${listening ? 'active' : ''}`}>
                {listening ? 'Listening' : speaking ? 'Speaking' : 'Ready'}
              </span>
              <div className="pampa-float-buttons">
                <button
                  type="button"
                  className={`pampa-float-action-btn ${listening ? 'active' : ''}`}
                  onClick={listening ? stopListening : startListening}
                  aria-pressed={listening}
                >
                  {listening ? <FaStop aria-hidden="true" /> : <FaMicrophone aria-hidden="true" />}
                </button>
                <button
                  type="button"
                  className="pampa-float-support"
                  onClick={createSupportTicket}
                  disabled={loading || !input.trim()}
                >
                  <span>Support</span>
                </button>
                <button
                  type="submit"
                  className="pampa-float-send"
                  disabled={loading || !input.trim()}
                >
                  {loading ? <FaSpinner className="spin" aria-hidden="true" /> : <FaPaperPlane aria-hidden="true" />}
                  <span>Send</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      <button
        type="button"
        className="pampa-float-launcher"
        aria-label="Open PAMPA assistant"
        onClick={() => setOpen((v) => !v)}
      >
        <FaCommentDots aria-hidden="true" />
      </button>
    </div>
  );
}
